import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Badge, message, Modal, Form, Input } from 'antd';
import { BellOutlined, CheckCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import api from '../api';

dayjs.extend(isToday);
dayjs.extend(isYesterday);

const tabItems = [
  { key: '', label: '全部' },
  { key: '1', label: '公告' },
  { key: '2', label: '审批' },
  { key: '3', label: '通知' },
];

const msgTypeConfig = {
  1: { icon: <BellOutlined />, color: '#1677ff', bg: '#e6f4ff', label: '公告' },
  2: { icon: <CheckCircleOutlined />, color: '#52c41a', bg: '#f6ffed', label: '审批' },
  3: { icon: <BellOutlined />, color: '#fa8c16', bg: '#fff7e6', label: '通知' },
};
const DETAIL_SPLIT = '||DETAIL||';

function parseMessageDetail(content) {
  const text = String(content || '');
  const idx = text.indexOf(DETAIL_SPLIT);
  if (idx < 0) return { summary: text, detail: null };
  const summary = text.slice(0, idx);
  const raw = text.slice(idx + DETAIL_SPLIT.length);
  try {
    return { summary, detail: JSON.parse(raw) };
  } catch {
    return { summary: text, detail: null };
  }
}

function getDateLabel(dateStr) {
  const d = dayjs(dateStr);
  if (d.isToday()) return '今天';
  if (d.isYesterday()) return '昨天';
  return d.format('MM月DD日');
}

function getStatusTag(item) {
  const parsed = parseMessageDetail(item.content || '');
  const content = (parsed.summary || '') + (item.title || '');
  if (/已通过/.test(content)) return { text: '已通过', bg: '#f6ffed', color: '#52c41a' };
  if (/待处理|待审批/.test(content)) return { text: '待处理', bg: '#fff7e6', color: '#fa8c16' };
  if (/已驳回/.test(content)) return { text: '已驳回', bg: '#fff2f0', color: '#ff4d4f' };
  if (/重要|紧急/.test(content) && item.msg_type === 1) return { text: '重要公告', bg: '#fff2f0', color: '#ff4d4f' };
  return null;
}

function groupByDate(items) {
  const groups = {};
  items.forEach(item => {
    const label = getDateLabel(item.create_time);
    if (!groups[label]) groups[label] = [];
    groups[label].push(item);
  });
  return Object.entries(groups);
}

function getMessageSummary(item) {
  const parsed = parseMessageDetail(item?.content || '');
  if (Number(item?.msg_type) === 2) {
    return parsed.summary || (item?.title ? `${item.title}` : '审批消息');
  }
  return parsed.summary || '-';
}

export default function Messages() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const permKeys = Array.isArray(user?.perm_keys) ? user.perm_keys : [];
  const canPublishAnnouncement = permKeys.includes('publish_announcement');
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('');
  const [unreadCounts, setUnreadCounts] = useState({ 2: 0 });
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishForm] = Form.useForm();
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailMessage, setDetailMessage] = useState(null);
  const [openDeleteId, setOpenDeleteId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const touchStartXRef = useRef(0);
  const touchMovedRef = useRef(false);

  const fetchMessages = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: 20 };
    if (activeTab) params.msg_type = activeTab;
    api.get('/messages', { params })
      .then(res => {
        const items = res?.items || [];
        setMessages(items);
        setTotal(res?.total || 0);
        // Count unread approval messages for badge
        const approvalUnread = items.filter(m => m.msg_type === 2 && !m.is_read).length;
        setUnreadCounts(prev => ({ ...prev, 2: approvalUnread }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, activeTab]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handlePublish = async () => {
    try {
      const values = await publishForm.validateFields();
      setPublishLoading(true);
      await api.post('/messages', values);
      message.success('公告发布成功');
      publishForm.resetFields();
      setPublishOpen(false);
      setActiveTab('1');
      setPage(1);
      fetchMessages();
    } catch (err) {
      if (err?.message) message.error(err.message);
    } finally {
      setPublishLoading(false);
    }
  };

  const markRead = async (item) => {
    if (item.is_read) return;
    try {
      await api.put(`/messages/${item.id}/read`);
      setMessages(prev => prev.map(m => m.id === item.id ? { ...m, is_read: true } : m));
      if (item.msg_type === 2) {
        setUnreadCounts(prev => ({ ...prev, 2: Math.max(0, prev[2] - 1) }));
      }
    } catch (err) {
      message.error('操作失败');
    }
  };

  const markAllRead = async () => {
    try {
      const params = {};
      if (activeTab) params.msg_type = Number(activeTab);
      const res = await api.put('/messages/read-all', {}, { params });
      const n = Number(res?.updated || 0);
      setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
      setUnreadCounts({ 2: 0 });
      message.success(n > 0 ? `已全部标为已读（${n}条）` : '没有未读消息');
    } catch (err) {
      message.error(err.message || '操作失败');
    }
  };

  const handleDeleteMessage = async (item) => {
    try {
      await api.delete(`/messages/mine/${item.id}`);
      setMessages(prev => prev.filter(m => m.id !== item.id));
      setTotal(prev => Math.max(0, Number(prev || 0) - 1));
      if (item.msg_type === 2 && !item.is_read) {
        setUnreadCounts(prev => ({ ...prev, 2: Math.max(0, Number(prev?.[2] || 0) - 1) }));
      }
      setOpenDeleteId(null);
      message.success('已删除');
    } catch (err) {
      message.error(err.message || '删除失败');
    }
  };

  const deleteAllRead = async () => {
    Modal.confirm({
      title: '确认删除',
      content: activeTab
        ? '将删除当前分类下全部已读消息（未读保留）'
        : '将删除全部已读消息（未读保留）',
      okText: '删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const params = {};
          if (activeTab) params.msg_type = Number(activeTab);
          const res = await api.delete('/messages/mine/read', { params });
          const n = Number(res?.deleted || 0);
          if (n <= 0) {
            message.info('暂无可删除的已读消息');
            return;
          }
          message.success(`已删除 ${n} 条已读消息`);
          setPage(1);
          fetchMessages();
        } catch (err) {
          message.error(err.message || '删除失败');
        }
      },
    });
  };

  const handleTouchStart = (e, itemId) => {
    touchStartXRef.current = e.touches?.[0]?.clientX || 0;
    touchMovedRef.current = false;
    setDraggingId(itemId);
    setDragOffset(openDeleteId === itemId ? -72 : 0);
  };

  const handleTouchMove = (e, itemId) => {
    if (draggingId !== itemId) return;
    const currentX = e.touches?.[0]?.clientX || 0;
    const dx = currentX - touchStartXRef.current;
    if (Math.abs(dx) > 6) touchMovedRef.current = true;
    const base = openDeleteId === itemId ? -72 : 0;
    const next = Math.max(-72, Math.min(0, base + dx));
    setDragOffset(next);
  };

  const handleTouchEnd = (itemId) => {
    if (draggingId !== itemId) return;
    const finalOpen = dragOffset <= -36;
    setOpenDeleteId(finalOpen ? itemId : null);
    setDraggingId(null);
    setDragOffset(0);
  };

  const dateGroups = groupByDate(messages);
  const hasMore = messages.length < total;

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', paddingBottom: 70 }}>
      <div style={{
        height: 56,
        background: 'linear-gradient(180deg, #1677FF 0%, #4096ff 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 12,
        color: '#fff',
      }}>
        <span style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>消息</span>
      </div>
      {/* Filter Tabs */}
      <div style={{ display: 'flex', background: '#fff', borderBottom: '1px solid #f0f0f0', padding: '0 8px' }}>
        {tabItems.map(tab => (
          <div
            key={tab.key}
            onClick={() => { setActiveTab(tab.key); setPage(1); }}
            style={{
              flex: 1,
              textAlign: 'center',
              padding: '12px 0',
              fontSize: 14,
              fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? '#1677ff' : '#666',
              borderBottom: activeTab === tab.key ? '2px solid #1677ff' : '2px solid transparent',
              cursor: 'pointer',
              position: 'relative',
            }}
          >
            {tab.label}
            {tab.key === '2' && unreadCounts[2] > 0 && (
              <Badge count={unreadCounts[2]} size="small" style={{ position: 'absolute', top: 6, marginLeft: 2 }} />
            )}
          </div>
        ))}
      </div>
      <div style={{ textAlign: 'right', padding: '10px 16px 4px' }}>
        {canPublishAnnouncement && (
          <span
            onClick={() => setPublishOpen(true)}
            style={{ fontSize: 14, color: '#1677ff', cursor: 'pointer', marginRight: 14 }}
          >
            发布公告
          </span>
        )}
        <span onClick={deleteAllRead} style={{ fontSize: 14, color: '#1677ff', cursor: 'pointer', marginRight: 14 }}>全部删除已读</span>
        <span onClick={markAllRead} style={{ fontSize: 14, color: '#1677ff', cursor: 'pointer' }}>全部已读</span>
      </div>

      {/* Message List */}
      <div style={{ padding: '12px 0' }}>
        {loading && messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>加载中...</div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#999', fontSize: 14 }}>暂无消息</div>
        ) : (
          dateGroups.map(([dateLabel, items]) => (
            <div key={dateLabel}>
              {/* Date Separator */}
              <div style={{ textAlign: 'center', padding: '8px 0', fontSize: 12, color: '#999' }}>
                <span style={{ background: '#eee', borderRadius: 10, padding: '2px 12px' }}>{dateLabel}</span>
              </div>

              {items.map(item => {
                const typeConf = msgTypeConfig[item.msg_type] || msgTypeConfig[3];
                const statusTag = getStatusTag(item);
                const showDelete = draggingId === item.id || openDeleteId === item.id;
                return (
                  <div
                    key={item.id}
                    style={{
                      position: 'relative',
                      margin: '0 16px 12px',
                      borderRadius: 12,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: 72,
                        background: '#ff4d4f',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 14,
                        cursor: 'pointer',
                        opacity: showDelete ? 1 : 0,
                        pointerEvents: showDelete ? 'auto' : 'none',
                        transition: 'opacity 0.2s ease',
                      }}
                      onClick={() => handleDeleteMessage(item)}
                    >
                      删除
                    </div>
                    <div
                      onTouchStart={(e) => handleTouchStart(e, item.id)}
                      onTouchMove={(e) => handleTouchMove(e, item.id)}
                      onTouchEnd={() => handleTouchEnd(item.id)}
                      onClick={() => {
                        if (touchMovedRef.current) return;
                        if (openDeleteId === item.id) {
                          setOpenDeleteId(null);
                          return;
                        }
                        markRead(item);
                        setDetailMessage(item);
                        setDetailOpen(true);
                      }}
                      style={{
                        background: '#fff',
                        borderRadius: 12,
                        padding: 16,
                        cursor: 'pointer',
                        position: 'relative',
                        transform: `translateX(${draggingId === item.id ? dragOffset : (openDeleteId === item.id ? -72 : 0)}px)`,
                        transition: draggingId === item.id ? 'none' : 'transform 0.2s ease',
                      }}
                    >
                      <div style={{ display: 'flex', gap: 12 }}>
                      {/* Icon */}
                      <div style={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: typeConf.bg, color: typeConf.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 18, flexShrink: 0,
                      }}>
                        {typeConf.icon}
                      </div>

                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                          <span style={{ fontSize: 15, fontWeight: 600, color: '#333', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>
                            {item.title}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                            <span style={{ fontSize: 12, color: '#999' }}>{dayjs(item.create_time).format('HH:mm')}</span>
                            {!item.is_read && (
                              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ff4d4f' }} />
                            )}
                          </div>
                        </div>
                        <div style={{
                          fontSize: 13, color: '#888', lineHeight: '20px',
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        }}>
                          {getMessageSummary(item)}
                        </div>
                        {statusTag && (
                          <div style={{ marginTop: 8 }}>
                            <span style={{
                              fontSize: 12, padding: '2px 8px', borderRadius: 4,
                              background: statusTag.bg, color: statusTag.color,
                            }}>
                              {statusTag.text}
                            </span>
                          </div>
                        )}
                      </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}

        {/* Load More */}
        {hasMore && !loading && (
          <div
            onClick={() => setPage(p => p + 1)}
            style={{ textAlign: 'center', padding: '12px 0', color: '#1677ff', fontSize: 14, cursor: 'pointer' }}
          >
            加载更多
          </div>
        )}
        {loading && messages.length > 0 && (
          <div style={{ textAlign: 'center', padding: '12px 0', color: '#999', fontSize: 13 }}>加载中...</div>
        )}
      </div>

      <Modal
        title="发布公告"
        open={publishOpen}
        onOk={handlePublish}
        onCancel={() => setPublishOpen(false)}
        confirmLoading={publishLoading}
        okText="发布"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={publishForm} layout="vertical">
          <Form.Item name="title" label="公告标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="请输入公告标题" />
          </Form.Item>
          <Form.Item name="content" label="公告内容" rules={[{ required: true, message: '请输入内容' }]}>
            <Input.TextArea rows={4} placeholder="请输入公告内容" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="消息详情"
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
      >
        {(() => {
          const parsed = parseMessageDetail(detailMessage?.content || '');
          const detail = parsed.detail || null;
          return (
        <div style={{ lineHeight: 1.8, color: '#333' }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>{detailMessage?.title || '-'}</div>
          <div style={{ color: '#666', marginBottom: 10 }}>{getMessageSummary(detailMessage)}</div>
          {detail && Number(detail.apply_type) === 1 && (
            <div style={{ color: '#333', fontSize: 14, lineHeight: 1.9, marginBottom: 10 }}>
              <div><b>借用资产名称：</b>{detail.asset_names || '-'}</div>
              <div><b>借用人：</b>{detail.borrower_name || '-'}</div>
              <div><b>申请时间：</b>{detail.apply_time
                ? dayjs(detail.apply_time).format('YYYY年MM月DD日 HH:mm')
                : '-'}</div>
              <div><b>审批人：</b>{detail.approver_name || '-'}</div>
              <div><b>审核时间：</b>{detail.approve_time
                ? dayjs(detail.approve_time).format('YYYY年MM月DD日 HH:mm')
                : '-'}</div>
            </div>
          )}
          {detail && Number(detail.apply_type) === 2 && (
            <div style={{ color: '#333', fontSize: 14, lineHeight: 1.9, marginBottom: 10 }}>
              {detail.asset_names ? <div><b>资产名称：</b>{detail.asset_names || '-'}</div> : null}
              <div><b>原使用人：</b>{detail.old_user_name || '-'}</div>
              <div><b>原存放地点：</b>{detail.old_location_name || '-'}</div>
              {detail.old_location_manager_names ? (
                <div><b>原存放地点负责人：</b>{detail.old_location_manager_names}</div>
              ) : null}
              <div><b>现使用人：</b>{detail.new_user_name || '-'}</div>
              <div><b>现存放地点：</b>{detail.new_location_name || '-'}</div>
              {detail.new_location_manager_names ? (
                <div><b>现存放地点负责人：</b>{detail.new_location_manager_names}</div>
              ) : null}
              <div><b>审批人：</b>{detail.approver_name || '-'}</div>
              <div><b>审批时间：</b>{detail.approve_time
                ? dayjs(detail.approve_time).format('YYYY年MM月DD日 HH:mm')
                : '-'}</div>
            </div>
          )}
          {detail && Number(detail.apply_type) === 3 && (
            <div style={{ color: '#333', fontSize: 14, lineHeight: 1.9, marginBottom: 10 }}>
              {(Array.isArray(detail.rows) ? detail.rows : []).map((row, idx) => (
                <div key={`${row.asset_name || 'asset'}-${idx}`} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: '1px dashed #f0f0f0' }}>
                  <div><b>资产名称：</b>{row.asset_name || '-'}</div>
                  <div><b>资产存放地点：</b>{row.location_name || '-'}</div>
                  {row.holder_name ? <div><b>使用人：</b>{row.holder_name}</div> : null}
                  {row.disposer_name ? (
                    <>
                      <div><b>处置人：</b>{row.disposer_name || '-'}</div>
                      <div><b>处置时间：</b>{row.dispose_time ? dayjs(row.dispose_time).format('YYYY年MM月DD日 HH:mm') : '-'}</div>
                    </>
                  ) : (
                    <>
                      <div><b>处置申请人：</b>{row.applicant_name || '-'}</div>
                      <div><b>申请时间：</b>{row.apply_time ? dayjs(row.apply_time).format('YYYY年MM月DD日 HH:mm') : '-'}</div>
                      <div><b>审核人：</b>{row.approver_name || '-'}</div>
                      <div><b>审核时间：</b>{row.approve_time ? dayjs(row.approve_time).format('YYYY年MM月DD日 HH:mm') : '-'}</div>
                      <div><b>审核状态：</b>{row.status_text || '-'}</div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          {detail && Number(detail.apply_type) === 4 && (
            <div style={{ color: '#333', fontSize: 14, lineHeight: 1.9, marginBottom: 10 }}>
              {(Array.isArray(detail.rows) ? detail.rows : []).map((row, idx) => (
                <div key={`${row.asset_name || 'asset'}-${idx}`} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: '1px dashed #f0f0f0' }}>
                  <div><b>资产名称：</b>{row.asset_name || '-'}</div>
                  <div><b>资产存放地点：</b>{row.location_name || '-'}</div>
                  {row.holder_name ? <div><b>使用人：</b>{row.holder_name}</div> : null}
                  <div><b>状态：</b>{row.restored ? '资产已被恢复' : (row.status_text || '-')}</div>
                  <div><b>申请人：</b>{row.applicant_name || '-'}</div>
                  <div><b>申请时间：</b>{row.apply_time ? dayjs(row.apply_time).format('YYYY年MM月DD日 HH:mm') : '-'}</div>
                  <div><b>审核人：</b>{row.approver_name || '-'}</div>
                  <div><b>审核时间：</b>{row.approve_time ? dayjs(row.approve_time).format('YYYY年MM月DD日 HH:mm') : '-'}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 12, color: '#999' }}>
            时间：{detailMessage?.create_time ? dayjs(detailMessage.create_time).format('YYYY-MM-DD HH:mm') : '-'}
          </div>
        </div>
          );
        })()}
      </Modal>
    </div>
  );
}
